import { Component, computed, input, OnInit, output, signal, viewChild } from '@angular/core';
import { delay } from '@gametailors/web-core/misc';
import { RidDraggable } from '../../components/rid-draggable/rid-draggable';
import { RidDropZone } from '../../components/rid-drop-zone/rid-drop-zone';
import { RidKlankCard } from '../../components/rid-klank-card/rid-klank-card';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidSpacer } from '../../components/rid-spacer/rid-spacer';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';

@Component({
  selector: 'rid-word-snake',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidOverlayFeedback,
    RidKlankCard,
    RidDraggable,
    RidDropZone,
    RidSpacer,
    RidMechanicTitle,
  ],
  templateUrl: './rid-word-snake.html',
  host: {
    class: 'block',
  },
})
export class RidWordSnake implements OnInit {
  snakes = input.required<string[][]>();

  snakeIndex = signal(0);
  showAssignment = signal(false);
  assignment = signal<{
    options: { word: string; available: boolean }[];
    snake: string[];
  }>({ options: [], snake: [] });
  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');
  dropZone = viewChild.required<RidDropZone>('dropZone');

  snakeComplete = computed(() => {
    const a = this.assignment();
    const currentSnake = this.snakes()[this.snakeIndex()];
    return currentSnake && a.snake.length >= currentSnake.length;
  });

  // Center the midpoint between the last word and the drop zone.
  // When complete, freeze so the last two words stay centered.
  snakeTransform = computed(() => {
    const n = this.assignment().snake.length;
    const total = this.snakes()[this.snakeIndex()]?.length ?? n;
    const effectiveN = n >= total ? n - 1 : n;
    const offset = 8.5 * (effectiveN - 1);
    return `translateX(-${offset}rem)`;
  });

  finished = output<void>();

  ngOnInit(): void {
    this.initializeSnake(this.snakeIndex());
    this.showAssignment.set(true);
  }

  private initializeSnake(index: number) {
    const snake = this.snakes()[index];
    const options = snake.slice(1).map((word) => ({ word, available: true }));
    for (let i = options.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [options[i], options[j]] = [options[j], options[i]];
    }
    this.assignment.set({
      options,
      snake: [snake[0]],
    });
  }

  onDragMove(draggable: RidDraggable, point: { x: number; y: number }) {
    this.dropZone().handleMove(draggable, point);
  }

  onDragRelease(draggable: RidDraggable, optionIndex: number) {
    if (this.dropZone().isSnapped(draggable)) {
      this.onDropped(draggable, optionIndex);
    }
  }

  onDropped(draggable: RidDraggable, optionIndex: number) {
    const a = this.assignment();
    const isCorrect = a.options[optionIndex].word === this.snakes()[this.snakeIndex()][a.snake.length];

    if (isCorrect) {
      const newOptions = a.options.map((o, i) => (i === optionIndex ? { ...o, available: false } : o));
      const newSnake = [...a.snake, a.options[optionIndex].word];
      this.assignment.set({ ...a, options: newOptions, snake: newSnake });
      this.overlayFeedback().showCorrect();

      if (newSnake.length >= this.snakes()[this.snakeIndex()].length) {
        delay(1000).then(() => this.goToNextSnake());
      }
    } else {
      draggable.snapToOrigin();
      this.overlayFeedback().showIncorrect();
    }
  }

  private goToNextSnake() {
    const nextIndex = this.snakeIndex() + 1;
    if (nextIndex < this.snakes().length) {
      // Hide current assignment (triggers leave animation)
      this.showAssignment.set(false);
      // Wait for leave animation (250ms) + buffer, then show new assignment
      delay(400).then(() => {
        this.snakeIndex.set(nextIndex);
        this.initializeSnake(nextIndex);
        this.showAssignment.set(true);
      });
    } else {
      this.finished.emit();
    }
  }
}
